import { z } from 'zod';

/**
 * 模型动态路由：跨模型兜底（docs/12 §2）。
 *
 * 同一模型在多个上游渠道之间的切换由选路器自动完成；这里配置的是「主模型所有渠道都不可用时改用哪个模型」。
 * 兜底只在客户明确同意时发生：请求里给出 fallback_models，或 allow_fallback=true 使用下面的平台默认兜底链。
 * 切换后任务对象的 model_used 写明实际模型，并按实际模型的价格结算。
 */

export const MAX_FALLBACK_MODELS = 3;

export const ModelFallbackChainSchema = z.object({
  model: z.string().trim().min(1).max(64),
  fallbacks: z.array(z.string().trim().min(1).max(64)).min(1).max(MAX_FALLBACK_MODELS),
});
export type ModelFallbackChain = z.infer<typeof ModelFallbackChainSchema>;

export const ModelRoutingSettingsSchema = z.object({
  /** 关闭后忽略 allow_fallback（请求里显式给出的 fallback_models 仍然生效） */
  enabled: z.boolean(),
  chains: z
    .array(ModelFallbackChainSchema)
    .max(50)
    .refine((list) => new Set(list.map((c) => c.model)).size === list.length, '同一模型只能配置一条兜底链')
    .refine((list) => list.every((c) => !c.fallbacks.includes(c.model)), '兜底模型不能是它自己')
    .refine((list) => list.every((c) => new Set(c.fallbacks).size === c.fallbacks.length), '兜底链里有重复模型'),
});
export type ModelRoutingSettings = z.infer<typeof ModelRoutingSettingsSchema>;

export const DEFAULT_MODEL_ROUTING: ModelRoutingSettings = { enabled: true, chains: [] };
